import { useEffect, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { Package, MapPin, Clock, CheckCircle, Plane, Truck, Home } from 'lucide-react';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TrackingPage = () => {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const trackingToken = searchParams.get('token');
  const { token, isAuthenticated, loading: authLoading } = useAuth();
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Wait for auth loading to complete before fetching
    if (!authLoading) {
      fetchTracking();
    }
  }, [orderId, trackingToken, authLoading]);

  const fetchTracking = async () => {
    try {
      // Try public tracking endpoint first (for guests with token)
      if (trackingToken) {
        const response = await axios.get(`${API}/public/tracking/${orderId}?token=${trackingToken}`);
        setTrackingData(response.data);
      } else if (isAuthenticated) {
        // For authenticated users (including admins), try to get full order
        const response = await axios.get(`${API}/orders/${orderId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const order = response.data;
        // Transform to tracking data format
        const tracking = order.maksura_tracking || {};
        setTrackingData({
          order_id: order.id,
          status: tracking.status || 'pending',
          route: tracking.route || [],
          updates: tracking.updates || [],
          estimated_delivery_days: tracking.estimated_delivery_days || 21,
          created_at: order.created_at,
          shipping_address: order.shipping_address,
          total_amount: order.total_amount,
          items: order.items,
          payment_status: order.payment_status,
          user_email: order.user_email || order.guest_email
        });
      } else {
        setError('Please login or use the tracking link from your email');
      }
    } catch (err) {
      console.error('Failed to fetch tracking', err);
      if (err.response?.status === 404) {
        setError('Order not found. Please check the order ID.');
      } else {
        setError(err.response?.data?.detail || 'Failed to load tracking information');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    const icons = {
      'picked_up': Package,
      'departed_origin': Plane,
      'in_transit': Plane,
      'arrived_country': MapPin,
      'local_hub': Truck,
      'out_for_delivery': Truck,
      'delivered': Home,
      'processing': Package,
    };
    return icons[status] || MapPin;
  };

  const getStatusColor = (isCompleted, isCurrent) => {
    if (isCompleted) return 'bg-green-600';
    if (isCurrent) return 'bg-blue-600 animate-pulse';
    return 'bg-gray-300';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white text-gray-900">
        <Navbar />
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
        </div>
      </div>
    );
  }

  if (error || !trackingData) {
    return (
      <div className="min-h-screen bg-white text-gray-900">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
          <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h1 className="font-heading text-3xl font-bold mb-4">Unable to Load Tracking</h1>
          <p className="text-gray-600 mb-8">{error || 'Order not found'}</p>
          <Link to="/">
            <Button className="bg-black text-white hover:bg-zinc-800">Back to Home</Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const route = trackingData.route || [];
  const updates = trackingData.updates || [];
  const currentStatus = trackingData.status || 'pending';
  const totalSteps = trackingData.total_steps || route.length || 4;
  const completedSteps = trackingData.completed_steps || updates.length;
  const progress = trackingData.progress || (totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0);

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <Navbar />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-12">
          <Link to="/" className="text-sm uppercase tracking-widest text-gray-500 hover:text-black mb-4 inline-block">
            ← Back to Home
          </Link>
          <h1 className="font-heading text-4xl font-bold mb-2" data-testid="tracking-title">Order Tracking</h1>
          <p className="text-gray-600">Order ID: {trackingData.order_id?.slice(0, 8)}...</p>
        </div>

        {/* Maksura Tracking Banner */}
        <div
          className="relative h-64 mb-12 overflow-hidden rounded-xl"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1706823871410-ed8b01faef7e?crop=entropy&cs=srgb&fm=jpg&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
          data-testid="maksura-banner"
        >
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <div className="text-center text-white">
              <h2 className="font-heading text-3xl font-bold mb-2">MAKSURA SHIPPING</h2>
              <p className="text-sm uppercase tracking-widest mb-4">
                Guangzhou, China → Your Location
              </p>
              {/* Progress Bar */}
              <div className="w-64 mx-auto">
                <div className="flex justify-between text-xs mb-1">
                  <span>Progress</span>
                  <span>{progress}%</span>
                </div>
                <div className="w-full bg-white/30 rounded-full h-2">
                  <div
                    className="bg-green-400 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Smart Route Display */}
        {route.length > 0 ? (
          <div className="bg-gray-50 p-8 mb-12 rounded-xl" data-testid="smart-route">
            <h2 className="font-heading text-2xl font-bold mb-8">Your Package Route</h2>
            
            <div className="relative">
              {/* Timeline Line */}
              <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-300"></div>

              {/* Route Steps */}
              <div className="space-y-6">
                {route.map((step, index) => {
                  const isCompleted = index < completedSteps;
                  const isCurrent = index === completedSteps - 1;
                  const Icon = getStatusIcon(step.status);
                  const update = updates[index];

                  return (
                    <div 
                      key={index} 
                      className={`relative flex items-start ${!isCompleted && !isCurrent ? 'opacity-50' : ''}`}
                      data-testid={`route-step-${index}`}
                    >
                      <div
                        className={`w-12 h-12 rounded-full flex items-center justify-center z-10 ${getStatusColor(isCompleted, isCurrent)}`}
                      >
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                      <div className="ml-6 flex-1 bg-white p-4 rounded-lg shadow-sm border">
                        <div className="flex justify-between items-start mb-2">
                          <h3 className="font-heading font-semibold text-lg">
                            {step.location}
                          </h3>
                          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">
                            {step.country}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{step.description}</p>
                        {update && (
                          <p className="text-xs text-gray-400">
                            {new Date(update.timestamp).toLocaleString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        )}
                        {isCurrent && (
                          <span className="inline-block mt-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                            Current Location
                          </span>
                        )}
                        {isCompleted && !isCurrent && (
                          <span className="inline-block mt-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                            Completed
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          /* Legacy Timeline for orders without smart route */
          <div className="bg-gray-50 p-8 mb-12 rounded-xl">
            <h2 className="font-heading text-2xl font-bold mb-8">Shipment Progress</h2>
            
            <div className="relative">
              <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-300"></div>

              <div className="space-y-8">
                {[
                  { status: 'pending_payment', label: 'Payment Confirmation', icon: Clock },
                  { status: 'processing', label: 'Processing & Packaging', icon: Package },
                  { status: 'in_transit', label: 'In Transit', icon: Plane },
                  { status: 'delivered', label: 'Delivered', icon: CheckCircle }
                ].map((step, index) => {
                  const statusOrder = ['pending_payment', 'processing', 'in_transit', 'delivered'];
                  const currentIndex = statusOrder.indexOf(currentStatus);
                  const stepStatus = index < currentIndex ? 'complete' : index === currentIndex ? 'current' : 'pending';
                  const Icon = step.icon;

                  return (
                    <div key={step.status} className="relative flex items-start" data-testid={`timeline-step-${step.status}`}>
                      <div
                        className={`w-12 h-12 rounded-full flex items-center justify-center z-10 ${
                          stepStatus === 'complete'
                            ? 'bg-green-600'
                            : stepStatus === 'current'
                            ? 'bg-blue-600'
                            : 'bg-gray-300'
                        }`}
                      >
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                      <div className="ml-6 flex-1">
                        <h3
                          className={`font-heading font-semibold mb-1 ${
                            stepStatus !== 'pending' ? 'text-black' : 'text-gray-400'
                          }`}
                        >
                          {step.label}
                        </h3>
                        {stepStatus === 'current' && (
                          <p className="text-sm text-gray-600">Current Status</p>
                        )}
                        {stepStatus === 'complete' && (
                          <p className="text-sm text-green-600">Completed</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Recent Updates */}
        {updates.length > 0 && (
          <div className="mb-12" data-testid="tracking-updates">
            <h2 className="font-heading text-2xl font-bold mb-6">Recent Updates</h2>
            <div className="space-y-4">
              {updates.slice().reverse().slice(0, 5).map((update, index) => (
                <div key={index} className="border-l-4 border-blue-600 pl-6 py-4 bg-gray-50 rounded-r-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="h-4 w-4 text-blue-600" />
                    <span className="font-medium">{update.location}</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-1">{update.description}</p>
                  <p className="text-xs text-gray-400">
                    {new Date(update.timestamp).toLocaleString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Order Details - only show if we have full order data */}
        {trackingData.shipping_address && (
          <div className="border border-gray-200 p-6 rounded-xl" data-testid="order-details">
            <h2 className="font-heading text-xl font-bold mb-6">Order Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-medium mb-2">Order Information</h3>
                <p className="text-sm text-gray-600">Order Date: {new Date(trackingData.created_at).toLocaleDateString()}</p>
                {trackingData.total_amount && (
                  <p className="text-sm text-gray-600">Total: ${trackingData.total_amount.toFixed(2)}</p>
                )}
                {trackingData.payment_status && (
                  <p className="text-sm text-gray-600">Payment: {trackingData.payment_status === 'paid' ? 'Paid' : 'Pending'}</p>
                )}
                <p className="text-sm text-gray-600">Est. Delivery: {trackingData.estimated_delivery_days || 21} days</p>
              </div>
              <div>
                <h3 className="font-medium mb-2">Shipping Address</h3>
                <p className="text-sm text-gray-600">{trackingData.shipping_address.full_name}</p>
                <p className="text-sm text-gray-600">{trackingData.shipping_address.address_line1}</p>
                <p className="text-sm text-gray-600">
                  {trackingData.shipping_address.city}, {trackingData.shipping_address.state} {trackingData.shipping_address.postal_code}
                </p>
                <p className="text-sm text-gray-600">{trackingData.shipping_address.country}</p>
              </div>
            </div>

            {trackingData.items && (
              <div className="mt-6">
                <h3 className="font-medium mb-4">Items ({trackingData.items.length})</h3>
                <div className="space-y-2">
                  {trackingData.items.map((item, index) => (
                    <div key={index} className="flex justify-between text-sm">
                      <span className="text-gray-600">{item.name || 'Item'} x{item.quantity}</span>
                      <span className="font-medium">${(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Estimated Delivery */}
        {currentStatus !== 'delivered' && (
          <div className="mt-12 text-center p-8 bg-blue-50 border border-blue-200 rounded-xl">
            <Clock className="h-12 w-12 mx-auto text-blue-600 mb-4" />
            <h3 className="font-heading text-xl font-bold mb-2">Estimated Delivery</h3>
            <p className="text-2xl font-bold text-blue-600">
              {trackingData.estimated_delivery_days || 21} Days
            </p>
            <p className="text-gray-600 mt-2">
              Your package is on its way from Guangzhou, China
            </p>
          </div>
        )}

        {/* Delivered Message */}
        {currentStatus === 'delivered' && (
          <div className="mt-12 text-center p-8 bg-green-50 border border-green-200 rounded-xl">
            <CheckCircle className="h-12 w-12 mx-auto text-green-600 mb-4" />
            <h3 className="font-heading text-xl font-bold mb-2">Package Delivered!</h3>
            <p className="text-gray-600">
              Your package was delivered successfully. Thank you for shopping with Pactac!
            </p>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default TrackingPage;
